import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import type { Stage } from "@mosaic/shared";
import { api, ApiError } from "../api/client.js";
import { IndexColumn, type RadarFilter } from "../components/IndexColumn.js";
import { Ask } from "../components/Ask.js";
import { Rail } from "../components/Rail.js";
import { askScopeFor, askScopeKey, askScopeNoun, readerAccession } from "../lib/askScope.js";
import { useShellLayout } from "../lib/shell.js";
import { useEdgeSwipe } from "../lib/swipe.js";

/**
 * The shell: rail + index + reading pane. Below the stylesheet's shell breakpoint the
 * reading pane covers the index rather than sitting beside it — the same master-detail
 * relationship, not a different app — and the rail becomes a bar along the bottom.
 * `showread` is what the stylesheet switches on.
 */
export function RadarLayout() {
  const location = useLocation();
  const [filter, setFilter] = useState<RadarFilter>({
    q: "",
    stage: [],
    archived: false,
    disposition: [],
  });
  const [debouncedQ, setDebouncedQ] = useState("");

  // Typing a ticker should not fire a query per keystroke against a substring search
  // that cannot use an index.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(filter.q), 180);
    return () => clearTimeout(timer);
  }, [filter.q]);

  const query = useQuery({
    queryKey: ["situations", debouncedQ, filter.stage, filter.archived, filter.disposition],
    queryFn: () =>
      api.situations({
        q: debouncedQ || undefined,
        stage: filter.stage.length ? (filter.stage as Stage[]) : undefined,
        // `false` is sent as absent rather than as `archived=false`: on the recovery views
        // an explicit `false` re-applies the archived filter and would hide the dismissed
        // rows they exist to find.
        archived: filter.archived || undefined,
        disposition: filter.disposition.length ? filter.disposition : undefined,
        limit: 200,
      }),
    // Keeps the previous list on screen while a filter change is in flight, so the
    // column does not blank out between keystrokes.
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const error = useMemo(() => {
    if (!query.isError) return null;
    const err = query.error as Error;
    if (err instanceof ApiError && err.isUnauthenticated) return "Signed out.";
    return err.message;
  }, [query.isError, query.error]);

  // Already fetched and cached by App; this reads it rather than refetching.
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const showread = location.pathname !== "/";
  const layout = useShellLayout();

  /**
   * On the desk the reader's rail collapses, giving the column of text its width back.
   * Collapsed it keeps the mark, which brings the rest back as an overlay: re-widening the
   * grid would reflow the text mid-read and lose the reader's place.
   *
   * On the phone the rail is a bar along the bottom and takes no width, so there is no
   * reflow to avoid and the collapse is a desk state only.
   */
  const readerRoute = location.pathname.startsWith("/filings/");
  const collapsed = readerRoute && layout === "desk";

  /**
   * Ask is a panel of the shell rather than a section of the page, and its open state
   * lives in the URL: the panel is opened from the page's own header while the layout
   * renders it, and a search parameter carries that across without a context and survives
   * a reload.
   *
   * Anchored to the right edge so that opening it does not push the content column
   * sideways and reflow the text being read.
   *
   * What it is asked of comes from the route through `askScopeFor`; where that answers
   * null, the panel does not open.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * The reader's filing, so a document scope can name the document on screen. Same query
   * key as the reader's, so the two share one request.
   */
  const accession = readerAccession(location.pathname);
  const readerFiling = useQuery({
    queryKey: ["filing", accession],
    queryFn: () => api.filing(accession!),
    enabled: accession !== null,
  });
  const askScope = useMemo(
    () => askScopeFor(location.pathname, searchParams, readerFiling.data?.documents),
    [location.pathname, searchParams, readerFiling.data],
  );
  const askOpen = searchParams.get("ask") === "1" && askScope !== null;
  const [railOpen, setRailOpen] = useState(false);
  // A navigation is the answer to the question the rail was opened to ask.
  useEffect(() => setRailOpen(false), [location.pathname]);

  const setAsk = (open: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (open) next.set("ask", "1");
    else next.delete("ask");
    setSearchParams(next, { replace: true });
  };

  /**
   * Ask is the pane one gesture to the right. Either direction is null where it would mean
   * nothing: no leftward swipe on a route with no subject, and no rightward one until the
   * panel is open. This is an addition to the keyboard-reachable controls, not a
   * replacement for them.
   */
  const swipe = useEdgeSwipe({
    left: askScope && !askOpen ? () => setAsk(true) : null,
    right: askOpen ? () => setAsk(false) : null,
  });

  return (
    <div
      className={`app${showread ? " showread" : ""}${readerRoute ? " reader" : ""}${
        askOpen ? " withask" : ""
      }`}
      {...swipe}
    >
      <Rail
        unguarded={me.data?.unguarded ?? false}
        collapsed={collapsed}
        open={railOpen}
        onToggle={() => setRailOpen((v) => !v)}
      />
      {/*
        Dismiss by pressing anywhere else, which is what an overlay owes the reader. It
        sits under the rail and over everything else, so the rail's own buttons still take
        the press.
      */}
      {collapsed && railOpen && (
        <div className="railscrim" onClick={() => setRailOpen(false)} aria-hidden="true" />
      )}
      <IndexColumn
        situations={query.data?.items ?? []}
        total={query.data?.total ?? 0}
        loading={query.isLoading}
        error={error}
        filter={filter}
        onFilter={setFilter}
        lastUpdated={query.dataUpdatedAt || null}
      />
      <Outlet />
      {/*
        The drawing of the gesture, and the way in for everyone who is not making it.

        A strip held at the edge with a tab attached, open on the side it continues from,
        so pressing and swiping read as one act. It is a real button: a gesture is not
        reachable from a keyboard, and the company page and the reader have no Ask control
        of their own.

        No hint text: the strip explains itself by being on screen, saying what is on the
        other side, and doing what the drag does. A line of prose would repeat on every
        page or need somewhere to remember that it had been read.

        Phone only. On the desk Ask is a fourth column with a toggle in the page header.
      */}
      {layout === "phone" && askScope && !askOpen && (
        <button className="askedge" aria-label="Ask" onClick={() => setAsk(true)}>
          <span className="askedge-tab" aria-hidden="true">
            Ask
            <Chevron />
          </span>
          {/* The strip's own name, riding down the edge, so the pane is not something
              you scrolled past once. Hidden from the accessible name, which the tab
              above already gives — one control must not announce itself twice. */}
          <span className="askedge-lbl" aria-hidden="true">
            Ask
          </span>
        </button>
      )}
      {/*
        The reciprocal, on the side the pane came from. The exit says where it lands
        rather than that it closes — "close" and "back to the situation" are the same act
        and only one of them tells you where you end up.
      */}
      {layout === "phone" && askOpen && askScope && (
        <button
          className="askedge back"
          aria-label={`Close Ask, back to the ${askScopeNoun(askScope).toLowerCase()}`}
          onClick={() => setAsk(false)}
        >
          {/*
            The chevron alone, where the way in carries the word. The strip is at the left
            margin here and a tab wide enough for a word would sit over the conversation;
            the destination is in the label below, which is why the exit is not a bare ✕.
          */}
          <span className="askedge-tab" aria-hidden="true">
            <Chevron back />
          </span>
          <span className="askedge-lbl" aria-hidden="true">
            {askScopeNoun(askScope)}
          </span>
        </button>
      )}
      {/*
        The fourth column. Keyed on the subject so navigating between two of them with the
        panel open starts a fresh conversation rather than carrying one subject's thread
        onto another's page — the scope is part of what a question means here, and a thread
        that quietly changed subject would be citing the wrong filings.
      */}
      {askOpen && askScope && (
        <aside className="askcol" aria-label="Ask">
          {/*
            The panel closes itself. On the phone the page gives up its column, taking the
            header toggle with it, and the toggle writes the parameter with `replace`, so
            Back does not undo it either; this control is then the only way out.
          */}
          <div className="askhd">
            <span className="sechd">Ask</span>
            <button className="askclose" aria-label="Close Ask" onClick={() => setAsk(false)}>
              Close
            </button>
          </div>
          <div className="askbody">
            <Ask key={askScopeKey(askScope)} scope={askScope} />
          </div>
        </aside>
      )}
    </div>
  );
}

/** The arrow on the tab, pointing the way the drag goes. */
function Chevron({ back = false }: { back?: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={back ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
    </svg>
  );
}
